/**
 * The game's music and sound effects (spec 07 §2-4): the C64 soundtrack and the C64's own effects,
 * played by a SID emulation in an AudioWorklet, in either the faithful "1987" sound or the remastered one.
 *
 *   const music = await MusicPlayer.create(audioContext);
 *   music.onEnded = (cue) => { ... };   // jingles (game over, extend, hurry up, ...) report their end
 *   music.play('title');
 *   music.effect('jump');               // on voice 1, on the tune's own 50 Hz frames (sfx/sfxPlayer.ts)
 *
 * The captured register streams come from `content/music/c64.json` (tools/capture-music.mjs) and
 * `content/sfx/*.json` (tools/capture-sfx.mjs).
 */

import music from '../../../content/music/c64.json';
import type { EffectName } from '../sfx/effectNames';
import type { SfxScript } from '../sfx/sfxPlayer';
import { loadEffects } from '../sfx/sfxTable';
import type { SidTune } from '../sid/sidTimeline';
import { EFFECT_CUES, decodeTune, isMusicCue, type MusicCue } from './musicCues';
import sidWorkletUrl from './sidWorklet.ts?worker&url';
import {
  SID_PROCESSOR, type FromWorklet, type SidProcessorOptions, type ToWorklet,
} from './workletProtocol';

export type { MusicCue } from './musicCues';
export type { EffectName } from '../sfx/effectNames';

/** '1987': the faithful 6581 sound, mono. 'remastered': the same notes, hi-fi and in stereo. */
export type MusicQuality = '1987' | 'remastered';

function decodeAll(): Map<MusicCue, SidTune> {
  const tunes = new Map<MusicCue, SidTune>();
  for (const s of music.subtunes) {
    if (!isMusicCue(s.cue)) throw new Error(`c64.json: unknown cue '${s.cue}'`);
    tunes.set(s.cue, decodeTune(s));
  }
  return tunes;
}

export class MusicPlayer {
  /** Called when a tune that ends (a jingle) has played its last frame. Looping tunes never end. */
  onEnded: ((cue: MusicCue) => void) | null = null;
  /** The worklet node; connected to the destination given to `create`. */
  readonly node: AudioWorkletNode;
  private readonly tunes: Map<MusicCue, SidTune>;
  private readonly effects: Map<EffectName, SfxScript>;
  private nextId = 1;
  private playing: { id: number; cue: MusicCue } | null = null;

  private constructor(node: AudioWorkletNode, tunes: Map<MusicCue, SidTune>, effects: Map<EffectName, SfxScript>) {
    this.node = node;
    this.tunes = tunes;
    this.effects = effects;
    node.port.onmessage = (e: MessageEvent<FromWorklet>) => {
      const p = this.playing;
      if (e.data.type === 'ended' && p !== null && e.data.id === p.id) {
        this.playing = null;
        this.onEnded?.(p.cue);
      }
    };
  }

  /** Load the worklet and connect it to `destination` (the context's output by default). */
  static async create(ctx: AudioContext, destination: AudioNode = ctx.destination,
    quality: MusicQuality = 'remastered'): Promise<MusicPlayer> {
    await ctx.audioWorklet.addModule(sidWorkletUrl);
    const processorOptions: SidProcessorOptions = {
      clockHz: music.clockHz,
      frameHz: music.frameHz,
      quality: quality === '1987' ? 'exact' : 'remastered',
    };
    const node = new AudioWorkletNode(ctx, SID_PROCESSOR, {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [2],
      processorOptions,
    });
    node.connect(destination);
    return new MusicPlayer(node, decodeAll(), loadEffects());
  }

  /** Start `cue` from its beginning, replacing whatever was playing. */
  play(cue: MusicCue): void {
    const tune = this.tunes.get(cue);
    if (tune === undefined) return;
    const id = this.nextId++;
    this.playing = { id, cue };
    this.post({
      type: 'play', id, init: tune.init, frames: tune.frames, loopFrame: tune.loopFrame, effects: EFFECT_CUES.has(cue),
    });
  }

  /** Stop the music (its notes release over a few ms). */
  stop(): void {
    this.playing = null;
    this.post({ type: 'stop' });
  }

  setQuality(quality: MusicQuality): void {
    this.post({ type: 'quality', quality: quality === '1987' ? 'exact' : 'remastered' });
  }

  /** Music volume, 0..1. At 0 ("music off") the tune still runs, silent, so the effects keep their clock. */
  setVolume(volume: number): void {
    this.post({ type: 'volume', volume });
  }

  /**
   * Play a sound effect on voice 1, from the next 50 Hz frame of the tune, by the C64's rules
   * (docs/research/sfx.md): only while an in-game tune plays (`introAndMain`, `main`, `bumblebee`,
   * `mainHurry`) - otherwise dropped; the jump first, then the last of the others asked for; one start
   * per 3 frames; an extra never cuts off one of the six. `effectsFor` (sfx/triggers.ts) says which.
   */
  effect(name: EffectName): void {
    const s = this.effects.get(name);
    if (s === undefined) return;
    this.post(s.waits === true
      ? { type: 'effect', name: s.name, lane: s.lane, frames: s.frames, waits: true }
      : { type: 'effect', name: s.name, lane: s.lane, frames: s.frames });
  }

  /** Effects volume, 0..1, apart from the music's. */
  setEffectsVolume(volume: number): void {
    this.post({ type: 'effectsVolume', volume });
  }

  /** The cue now playing, or null (stopped, or a jingle has ended). */
  get cue(): MusicCue | null {
    return this.playing?.cue ?? null;
  }

  dispose(): void {
    this.node.port.onmessage = null;
    this.node.disconnect();
  }

  private post(msg: ToWorklet): void {
    this.node.port.postMessage(msg);
  }
}
